import React from 'react';
import { motion } from 'framer-motion';

export default function Pagination({ page, totalPages, onPageChange }) {
  if (totalPages <= 1) return null;

  const pages = [];
  const range = 2;
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || (i >= page - range && i <= page + range)) {
      pages.push(i);
    }
  }

  // Add ellipses
  const withEllipsis = [];
  let prev = null;
  for (const p of pages) {
    if (prev !== null && p - prev > 1) withEllipsis.push('...');
    withEllipsis.push(p);
    prev = p;
  }

  const btnBase = {
    width: 40, height: 40,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    borderRadius: 12,
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'var(--font-display)',
    fontWeight: 700,
    fontSize: 14,
    transition: 'all 0.2s',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        marginTop: 48,
        flexWrap: 'wrap',
      }}
    >
      {/* Prev */}
      <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        disabled={page === 1}
        onClick={() => onPageChange(page - 1)}
        style={{
          ...btnBase,
          width: 'auto',
          padding: '0 16px',
          background: page === 1 ? 'var(--sindhi-cream)' : 'white',
          color: page === 1 ? 'var(--sindhi-text-muted)' : 'var(--sindhi-navy)',
          border: '1px solid var(--sindhi-cream)',
          boxShadow: page === 1 ? 'none' : '0 4px 12px rgba(0,0,0,0.06)',
          opacity: page === 1 ? 0.5 : 1,
          gap: 6,
        }}
      >
        ← Prev
      </motion.button>

      {/* Page Numbers */}
      {withEllipsis.map((p, i) =>
        p === '...' ? (
          <span key={`e-${i}`} style={{ color: 'var(--sindhi-text-muted)', padding: '0 4px' }}>…</span>
        ) : (
          <motion.button
            key={p}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => onPageChange(p)}
            style={{
              ...btnBase,
              background: page === p
                ? 'linear-gradient(135deg, var(--sindhi-maroon), var(--sindhi-maroon-dark))'
                : 'white',
              color: page === p ? 'white' : 'var(--sindhi-navy)',
              boxShadow: page === p
                ? '0 8px 20px -4px var(--sindhi-maroon-glow)'
                : '0 2px 8px rgba(0,0,0,0.05)',
              border: page === p ? 'none' : '1px solid var(--sindhi-cream)',
              transform: page === p ? 'translateY(-2px)' : 'none',
            }}
          >
            {p}
          </motion.button>
        )
      )}

      {/* Next */}
      <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        disabled={page === totalPages}
        onClick={() => onPageChange(page + 1)}
        style={{
          ...btnBase,
          width: 'auto',
          padding: '0 16px',
          background: page === totalPages ? 'var(--sindhi-cream)' : 'white',
          color: page === totalPages ? 'var(--sindhi-text-muted)' : 'var(--sindhi-navy)',
          border: '1px solid var(--sindhi-cream)',
          boxShadow: page === totalPages ? 'none' : '0 4px 12px rgba(0,0,0,0.06)',
          opacity: page === totalPages ? 0.5 : 1,
          gap: 6,
        }}
      >
        Next →
      </motion.button>
    </motion.div>
  );
}
